import { createContext, useContext, useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../utils/apiConfig";
import { getAuthHeaders, hasValidTokens } from "../utils/tokenManager";
import { clearListCaches } from "../api/api";
import { isNative } from "../utils/platform";
import { getAttribution, clearAttribution } from "../utils/attribution";
import { logger } from "../utils/logger";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // Timestamp-ul ultimei sincronizări de profil de la server (profile/me).
  // checkPremium îl folosește ca să sară peste un /api/auth/me redundant imediat
  // după login (profilul tocmai a fost adus de ensureProfile/loadProfile).
  const lastProfileSyncRef = useRef(0);

  const ensureProfile = async (accessToken, userMeta) => {
    const metaName = userMeta?.user_metadata?.name || userMeta?.name || '';
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ name: metaName })
      });
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        localStorage.setItem("user", JSON.stringify(profile));
        lastProfileSyncRef.current = Date.now();
        return profile;
      } else {
        console.warn("ensureProfile non-ok:", res.status);
        const cached = localStorage.getItem("user");
        if (cached) {
          try { const p = JSON.parse(cached); setUser(p); return p; } catch {}
        }
      }
    } catch (err) {
      console.error("Eroare la ensureProfile:", err);
      const cached = localStorage.getItem("user");
      if (cached) {
        try { const p = JSON.parse(cached); setUser(p); return p; } catch {}
      }
    }
    return null;
  };

  const loadProfile = async (accessToken) => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        localStorage.setItem("user", JSON.stringify(profile));
        lastProfileSyncRef.current = Date.now();
        return profile;
      } else {
        console.warn("loadProfile non-ok:", res.status);
        const cached = localStorage.getItem("user");
        if (cached) {
          try { const p = JSON.parse(cached); setUser(p); return p; } catch {}
        }
      }
    } catch (err) {
      console.error("Eroare la loadProfile:", err);
      const cached = localStorage.getItem("user");
      if (cached) {
        try { const p = JSON.parse(cached); setUser(p); return p; } catch {}
      }
    }
    return null;
  };

  const refreshUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      return await loadProfile(session.access_token);
    }
    return null;
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session?.user?.email) {
          const deletedEmail = localStorage.getItem("nativus_account_deleted");
          if (deletedEmail && deletedEmail.toLowerCase() === session.user.email.toLowerCase()) {
            console.warn("🚫 Blocked auto-login for deleted account:", session.user.email);
            localStorage.removeItem("nativus_account_deleted");
            await supabase.auth.signOut({ scope: 'local' });
            setUser(null);
            setLoading(false);
            return;
          }
        }

        if (event === 'INITIAL_SESSION') {
          if (session?.access_token) {
            await loadProfile(session.access_token);
          }
          setLoading(false);
          return;
        }

        if (event === 'SIGNED_IN') {
          if (session?.access_token) {
            const profile = await ensureProfile(session.access_token, session.user);
            const uid = session.user?.id;
            const attribution = getAttribution();
            if (uid) {
              // GDPR: identificăm userul doar prin UUID-ul Supabase (pseudonim),
              // fără email/nume. Attribution = doar parametri UTM (non-PII).
              posthog.identify(uid, {
                ...(attribution || {}),
              });
              Sentry.setUser({ id: uid });
            }
            if (attribution) {
              // Fire-and-forget: nu blocăm navigarea spre onboarding/home cât
              // timp se salvează attribution. clearAttribution() doar la succes,
              // ca să putem reîncerca dacă request-ul eșuează.
              fetch(`${getApiBaseUrl()}/api/auth/attribution`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify(attribution)
              })
                .then((res) => { if (res.ok) clearAttribution(); })
                .catch((e) => console.warn('Attribution save failed:', e.message));
            }
            if (!profile) {
              console.warn("🔐 ensureProfile returned null, setting fallback user from session");
              const fallback = {
                id: session.user?.id,
                email: session.user?.email,
                nume: session.user?.user_metadata?.name || '',
                premium: false
              };
              setUser(fallback);
              localStorage.setItem("user", JSON.stringify(fallback));
            }

            const path = window.location.pathname || '/';
            const isEntryPath =
              path === '/' ||
              path.startsWith('/login') ||
              path.startsWith('/register') ||
              path.startsWith('/verify') ||
              path.startsWith('/intro') ||
              path.startsWith('/onboarding');

            // După login (inclusiv OAuth native): nu folosim location.reload — ProtectedRoute
            // vede user=null înainte ca sesiunea să fie gata. Navigare SPA după ce user e setat.
            if (isEntryPath) {
              if (profile && !profile.onboarding_completed) {
                try {
                  const {
                    bumpEntryIfNeeded,
                    getOnboardingState,
                    shouldShowOnboarding,
                  } = await import('../utils/onboardingGate');
                  bumpEntryIfNeeded(profile.id);
                  const state = getOnboardingState(profile.id);
                  if (shouldShowOnboarding(state) && !path.startsWith('/onboarding')) {
                    navigate('/onboarding', { replace: true });
                  } else if (!path.startsWith('/onboarding')) {
                    navigate('/home', { replace: true });
                  }
                } catch (e) {
                  console.warn('onboarding gate check failed:', e?.message);
                  navigate('/home', { replace: true });
                }
              } else if (profile?.onboarding_completed) {
                navigate('/home', { replace: true });
              } else if (!profile) {
                queueMicrotask(() => navigate('/home', { replace: true }));
              }
            }
          }
          setLoading(false);
          return;
        }

        if (event === 'TOKEN_REFRESHED') {
          if (session?.access_token) {
            await loadProfile(session.access_token);
          }
          return;
        }

        if (event === 'SIGNED_OUT') {
          try {
            let logoutUserId = null;
            const cached = localStorage.getItem('user');
            if (cached) logoutUserId = JSON.parse(cached).id;
            const { unregisterPushOnLogout } = await import('../services/notifications');
            await unregisterPushOnLogout(logoutUserId);
          } catch (e) {
            console.warn('Push unregister on SIGNED_OUT skip:', e?.message);
          }
          posthog.reset();
          Sentry.setUser(null);
          try {
            const { logoutPurchases } = await import("../services/purchases");
            await logoutPurchases();
          } catch (e) {
            console.warn("RC logout skip:", e?.message || e);
          }
          clearListCaches();
          // Resetăm sentinel-ul de "ultima sesiune" al onboarding gate-ului ca
          // la următorul login (chiar în același JS context) entries să se
          // incrementeze. Altfel, logout+login consecutiv lasă entries la 1
          // și onboarding apare la fiecare logare.
          try {
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const k = localStorage.key(i);
              if (k && k.startsWith('nv_onboarding_last_session_')) {
                localStorage.removeItem(k);
              }
            }
          } catch {}
          setUser(null);
          localStorage.removeItem("user");
          localStorage.removeItem("fcmToken");
          setLoading(false);
          return;
        }
      }
    );

    const timeout = setTimeout(() => {
      const cached = localStorage.getItem("user");
      if (cached) {
        try { setUser(JSON.parse(cached)); } catch {}
      }
      setLoading(false);
    }, 5000);

    if (isNative()) {
      let listenerHandle;
      Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser')
      ]).then(([{ App }, { Browser }]) => {
        const handleOAuthCallback = async (url, source) => {
          if (!url?.startsWith('ro.nativus.app://login-callback')) return;
          Sentry.addBreadcrumb({
            category: 'auth',
            message: `OAuth deep link received (${source})`,
            level: 'info',
          });
          try {
            const parsedUrl = new URL(url.replace('ro.nativus.app://login-callback', 'https://nativus.app/login-callback'));
            const hashParams = new URLSearchParams(parsedUrl.hash ? parsedUrl.hash.slice(1) : '');
            const queryParams = parsedUrl.searchParams;

            // 1) Provider-ul (Apple/Google) sau Supabase au intors eroare?
            const oauthError = hashParams.get('error') || queryParams.get('error');
            const oauthErrorDesc =
              hashParams.get('error_description') ||
              queryParams.get('error_description');
            if (oauthError) {
              const msg = `OAuth provider error: ${oauthError}${oauthErrorDesc ? ' — ' + oauthErrorDesc : ''}`;
              console.error('[OAuth]', msg);
              Sentry.captureMessage(msg, {
                level: 'error',
                tags: { flow: 'oauth_callback', source },
                extra: { oauthError, oauthErrorDesc },
              });
              try { await Browser.close(); } catch {}
              try {
                window.dispatchEvent(new CustomEvent('nativus:oauth-error', {
                  detail: { error: oauthError, description: oauthErrorDesc },
                }));
              } catch {}
              return;
            }

            // 2) Implicit flow (default): tokens in fragment
            const access_token = hashParams.get('access_token') || queryParams.get('access_token');
            const refresh_token = hashParams.get('refresh_token') || queryParams.get('refresh_token');
            // 3) PKCE flow (fail-safe pentru cazul in care Supabase ar trece pe pkce):
            // codul vine in query si trebuie schimbat pentru sesiune.
            const code = queryParams.get('code') || hashParams.get('code');

            if (access_token && refresh_token) {
              const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
              if (setErr) {
                console.error('[OAuth] setSession failed:', setErr);
                Sentry.captureException(setErr, { tags: { flow: 'oauth_setsession', source } });
                try { await Browser.close(); } catch {}
                try {
                  window.dispatchEvent(new CustomEvent('nativus:oauth-error', {
                    detail: { error: 'set_session_failed', description: setErr.message },
                  }));
                } catch {}
                return;
              }
              Sentry.addBreadcrumb({ category: 'auth', message: 'setSession OK', level: 'info' });
            } else if (code) {
              const { error: exErr } = await supabase.auth.exchangeCodeForSession(code);
              if (exErr) {
                console.error('[OAuth] exchangeCodeForSession failed:', exErr);
                Sentry.captureException(exErr, { tags: { flow: 'oauth_pkce_exchange', source } });
                try { await Browser.close(); } catch {}
                try {
                  window.dispatchEvent(new CustomEvent('nativus:oauth-error', {
                    detail: { error: 'pkce_exchange_failed', description: exErr.message },
                  }));
                } catch {}
                return;
              }
              Sentry.addBreadcrumb({ category: 'auth', message: 'exchangeCodeForSession OK', level: 'info' });
            } else {
              const msg = 'OAuth deep link missing tokens and code';
              console.warn('[OAuth]', msg);
              Sentry.captureMessage(msg, {
                level: 'warning',
                tags: { flow: 'oauth_callback', source },
                extra: {
                  hashKeys: [...hashParams.keys()],
                  queryKeys: [...queryParams.keys()],
                },
              });
              try { await Browser.close(); } catch {}
              try {
                window.dispatchEvent(new CustomEvent('nativus:oauth-error', {
                  detail: { error: 'no_tokens', description: 'Server did not return session tokens' },
                }));
              } catch {}
              return;
            }

            try { await Browser.close(); } catch {}
            // onAuthStateChange (SIGNED_IN) navigheaza la /onboarding sau /home.
          } catch (err) {
            console.error('[OAuth] Deep link handler crashed:', err);
            Sentry.captureException(err, { tags: { flow: 'oauth_deeplink', source } });
            try { await Browser.close(); } catch {}
            try {
              window.dispatchEvent(new CustomEvent('nativus:oauth-error', {
                detail: { error: 'deeplink_crashed', description: err?.message },
              }));
            } catch {}
          }
        };

        App.getLaunchUrl()
          .then(({ url }) => handleOAuthCallback(url, 'launchUrl'))
          .catch(() => {});

        App.addListener('appUrlOpen', ({ url }) => {
          handleOAuthCallback(url, 'appUrlOpen');
        }).then((handle) => {
          listenerHandle = handle;
        });
      }).catch(() => {});

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeout);
        listenerHandle?.remove?.();
      };
    }

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate]);

  useEffect(() => {
    if (!isNative()) {
      import("../services/notifications").then(({ setupForegroundMessageListener }) => {
        setupForegroundMessageListener((payload) => {
          logger.info("Notificare primită:", payload);
        });
      }).catch(err => console.warn("Foreground listener skip:", err));
    }
  }, []);

  useEffect(() => {
    // Auto-register only for users who already consented — new users go through
    // NotificationPrompt first (30s după prima vizită Home) before we touch OS perms.
    if (user?.id && hasValidTokens() && localStorage.getItem('nativus_notif_consent') === 'granted') {
      import("../services/notifications").then(({ autoRegisterNotifications }) => {
        autoRegisterNotifications(user.id).catch((err) => {
          console.warn("Nu s-a putut înregistra token-ul FCM:", err);
        });
      }).catch(err => console.warn("Notifications skip:", err));
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && isNative()) {
      import("../services/purchases").then(({ initPurchases }) => {
        initPurchases(user.id).catch((err) => {
          console.warn("Nu s-a putut inițializa RevenueCat:", err);
        });
      }).catch(err => console.warn("RevenueCat skip:", err));
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const PROFILE_SYNC_DEBOUNCE_MS = 60000;
    const checkPremium = async () => {
      if (!hasValidTokens()) return;
      // Sărim peste /me dacă profilul a fost adus recent (ex. imediat după login
      // prin ensureProfile/loadProfile). Polling-ul la 5 min și revenirile în
      // app după mai mult timp trec normal.
      if (Date.now() - lastProfileSyncRef.current < PROFILE_SYNC_DEBOUNCE_MS) return;
      const headers = getAuthHeaders();
      try {
        const res = await fetch(
          `${getApiBaseUrl()}/api/auth/me`,
          { headers: { 'Content-Type': 'application/json', ...headers } }
        );
        if (res.ok) {
          const fresh = await res.json();
          lastProfileSyncRef.current = Date.now();
          if ((user.premium === true) !== (fresh.premium === true)) {
            setUser(fresh);
            localStorage.setItem("user", JSON.stringify(fresh));
          }
        }
      } catch {}
    };
    // Polling pauzat când appul e în fundal, ca să nu consumăm baterie/date inutil;
    // verificăm imediat și repornim polling-ul când userul revine în app.
    let interval = null;
    const startInterval = () => {
      if (interval) return;
      interval = setInterval(checkPremium, 300000);
    };
    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPremium();
        startInterval();
      } else {
        stopInterval();
      }
    };
    if (document.visibilityState === 'visible') startInterval();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
